import { useEffect, useState, type ReactNode } from "react";
import { ArrowDownUp, Box, Check, ChevronDown, CircleDashed, Search, Tag, X } from "lucide-react";
import { PRIORITY_LABEL, STATUS_LABEL, TASK_STATUSES, type Priority, type TaskStatus } from "@desk/contracts";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PriorityIcon, ProjectGlyph, StatusIcon } from "@/components/icons";
import { useHierarchy } from "@/data/hooks";
import { activeFilterCount, constrainFilters, SORT_LABEL, type SortKey, type TaskFilters } from "@/lib/filters";
import { cn } from "@/lib/utils";
import { PRIORITY_ORDER } from "@/features/task/TaskMenu";
import { SpaceIcon } from "@/components/container-icon";

const pill =
  "inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[13px] text-muted-foreground transition-colors hover:bg-hover hover:text-foreground data-[active=true]:bg-selected data-[active=true]:text-foreground [&_svg]:size-3.5 [&_svg]:shrink-0";

function FilterMenu({ label, active, icon, children }: { label: string; active: boolean; icon: ReactNode; children: ReactNode }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={pill} data-active={active}>
          {icon}
          <span className="max-w-[160px] truncate">{label}</span>
          <ChevronDown className="!size-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[360px] overflow-y-auto">{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface FilterBarProps {
  filters: TaskFilters;
  onChange: (f: TaskFilters) => void;
  /** Which container filters to show. Locked levels are fixed by the screen (e.g. space view). */
  containers: { area?: boolean; space?: boolean; project?: boolean };
  defaults: TaskFilters;
  labels: string[];
  sorts?: SortKey[];
  /** Extra controls on the right (view toggle). */
  trailing?: ReactNode;
}

export function FilterBar({ filters: f, onChange, containers, defaults, labels, sorts = ["priority", "deadline", "created", "manual"], trailing }: FilterBarProps) {
  const h = useHierarchy();
  const [q, setQ] = useState(f.q ?? "");
  useEffect(() => setQ(f.q ?? ""), [f.q]);
  useEffect(() => {
    const id = setTimeout(() => {
      if ((f.q ?? "") !== q) onChange({ ...f, q: q || undefined });
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const set = (patch: Partial<TaskFilters>) => onChange(constrainFilters({ ...f, ...patch }, h.spaces, h.projects));
  const spaces = f.areaId ? h.spacesOf(f.areaId) : h.spaces;
  const projects = h.projects.filter((p) => (!f.spaceId || p.spaceId === f.spaceId) && (!f.areaId || p.areaId === f.areaId));
  const toggle = <T,>(list: T[] | undefined, v: T) => {
    const cur = list ?? [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    return next.length ? next : undefined;
  };
  const count = activeFilterCount(f, defaults);

  return (
    <div className="flex flex-wrap items-center gap-1" role="toolbar" aria-label="Filters">
      <label className={cn(pill, "w-[118px] cursor-text gap-1.5 focus-within:bg-hover focus-within:text-foreground")}>
        <Search />
        <input
          aria-label="Filter by text"
          placeholder="Filter"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setQ("")}
          className="h-full min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
        />
      </label>
      {containers.area && (
        <FilterMenu label={h.area(f.areaId)?.name ?? "Area"} active={!!f.areaId && f.areaId !== defaults.areaId} icon={<Box />}>
          <DropdownMenuRadioGroup value={f.areaId ?? ""} onValueChange={(v) => set({ areaId: v || undefined })}>
            <DropdownMenuRadioItem value="">Any area</DropdownMenuRadioItem>
            {h.areas.map((a) => (
              <DropdownMenuRadioItem key={a.id} value={a.id}>
                {a.name}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </FilterMenu>
      )}
      {containers.space && (
        <FilterMenu label={h.space(f.spaceId)?.name ?? "Space"} active={!!f.spaceId && f.spaceId !== defaults.spaceId} icon={<SpaceIcon space={h.space(f.spaceId)} name="S" className="size-3.5 text-[8px]" />}>
          <DropdownMenuRadioGroup value={f.spaceId ?? ""} onValueChange={(v) => set({ spaceId: v || undefined })}>
            <DropdownMenuRadioItem value="">Any space</DropdownMenuRadioItem>
            {spaces.map((s) => (
              <DropdownMenuRadioItem key={s.id} value={s.id}>
                {s.name}
                {!f.areaId && <span className="text-[12px] text-subtle">{h.area(s.areaId)?.name}</span>}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </FilterMenu>
      )}
      {containers.project && (
        <FilterMenu
          label={f.projectId === "none" ? "No project" : (h.project(f.projectId)?.name ?? "Project")}
          active={!!f.projectId && f.projectId !== defaults.projectId}
          icon={<ProjectGlyph />}
        >
          <DropdownMenuRadioGroup value={f.projectId ?? ""} onValueChange={(v) => set({ projectId: v || undefined })}>
            <DropdownMenuRadioItem value="">Any project</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="none">No project</DropdownMenuRadioItem>
            {projects.length > 0 && <DropdownMenuSeparator />}
            {projects.map((p) => (
              <DropdownMenuRadioItem key={p.id} value={p.id}>
                {p.name}
                {!f.spaceId && <span className="text-[12px] text-subtle">{h.space(p.spaceId)?.name}</span>}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </FilterMenu>
      )}
      <FilterMenu
        label={f.status?.length ? f.status.map((s) => STATUS_LABEL[s]).join(", ") : "Status"}
        active={!!f.status?.length}
        icon={f.status?.length === 1 ? <StatusIcon status={f.status[0]!} /> : <CircleDashed />}
      >
        {TASK_STATUSES.map((s: TaskStatus) => (
          <DropdownMenuCheckboxItem
            key={s}
            checked={!!f.status?.includes(s)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => set({ status: toggle(f.status, s) })}
          >
            <StatusIcon status={s} />
            {STATUS_LABEL[s]}
          </DropdownMenuCheckboxItem>
        ))}
      </FilterMenu>
      <FilterMenu
        label={f.priority?.length ? f.priority.map((p) => PRIORITY_LABEL[p]).join(", ") : "Priority"}
        active={!!f.priority?.length}
        // A single chosen priority shows in its color; the generic "Priority" glyph stays neutral.
        icon={f.priority?.length === 1 ? <PriorityIcon priority={f.priority[0]!} /> : <PriorityIcon priority="high" className="text-current" />}
      >
        {PRIORITY_ORDER.map((p: Priority) => (
          <DropdownMenuCheckboxItem
            key={p}
            checked={!!f.priority?.includes(p)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => set({ priority: toggle(f.priority, p) })}
          >
            <PriorityIcon priority={p} />
            {PRIORITY_LABEL[p]}
          </DropdownMenuCheckboxItem>
        ))}
      </FilterMenu>
      {labels.length > 0 && (
        <FilterMenu label={f.label ?? "Label"} active={!!f.label} icon={<Tag />}>
          <DropdownMenuRadioGroup value={f.label ?? ""} onValueChange={(v) => set({ label: v || undefined })}>
            <DropdownMenuRadioItem value="">Any label</DropdownMenuRadioItem>
            {labels.map((l) => (
              <DropdownMenuRadioItem key={l} value={l}>
                {l}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </FilterMenu>
      )}
      {count > 0 && (
        <button
          type="button"
          className={cn(pill, "text-subtle")}
          onClick={() => onChange({ ...defaults, sort: f.sort, showCompleted: f.showCompleted })}
        >
          <X />
          Clear
        </button>
      )}
      <div className="ml-auto flex items-center gap-1">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button type="button" className={pill} aria-label={`Sort by ${SORT_LABEL[f.sort]}`}>
              <ArrowDownUp />
              {SORT_LABEL[f.sort]}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup value={f.sort} onValueChange={(v) => set({ sort: v as SortKey })}>
              {sorts.map((s) => (
                <DropdownMenuRadioItem key={s} value={s}>
                  {SORT_LABEL[s]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => set({ showCompleted: !f.showCompleted })}>
              {f.showCompleted ? <Check className="!text-foreground" /> : <span className="size-4" />}
              Show completed
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {trailing}
      </div>
    </div>
  );
}
